/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState } from 'react';
import { message, Modal, Checkbox, Tooltip } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';

import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { CreateTenderAnswer } from './CreateTenderAnswer';
import {
  AnswerFormValues,
  defaultAnswerValues,
} from '~components/atoms/Questions/AnswerQuickForm';
import { appLog } from '~src/utils/log';
import { queryCache, useMutation } from 'react-query';
import {
  addAnswerToQuestion,
  updateQuestion,
} from '~src/services/api/questions';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { DbQuestion } from '~src/types/db';
import { useProjectId } from '~src/hooks/useProjectId';
import QuestionForm, {
  QuestionFormValues,
} from '~components/atoms/Questions/QuestionForm';
import { manageCurrentCorrectionLetterQuestions } from '~src/services/api/movements';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export type ReviseQuestionModalProps = Pick<ModalProps, 'visible'> & {
  question: DbQuestion;
  onCancel?: () => void;
};

export const ReviseQuestionModal: React.FC<ReviseQuestionModalProps> = ({
  onCancel,
  question,
  ...modalProps
}) => {
  const intl = useIntl();

  const projectId = useProjectId();
  const tenderIdMatch = window?.location?.pathname?.match(/tenders\/(\d+)/);
  const tenderId = tenderIdMatch ? String(tenderIdMatch[1]) : '';

  const [
    reviseQuestionFormValues,
    setReviseQuestionFormValues,
  ] = useState<QuestionFormValues>({
    title: question.question,
    description: ``,
    question: question.question,
  });

  const [addToLetter, setAddToLetter] = useState(true);

  const [updateQuestionMutation, { isLoading }] = useMutation(updateQuestion, {
    onSuccess: () => {
      queryCache.invalidateQueries([
        QueryKeys.sharedTenderQuestion,
        // shareParams,
      ]);
      queryCache.invalidateQueries([
        QueryKeys.question,
        // shareParams,
      ]);
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'questions-answering-failed' }));
      appLog('addAnswerMutation -> item', item);
      appLog('addAnswerMutation -> err', error);
    },
  });

  const [doManageQuestionsToLetter] = useMutation(
    manageCurrentCorrectionLetterQuestions
  );

  const resetForm = () => {
    setReviseQuestionFormValues({
      title: question.question,
      description: ``,
      question: question.question,
    });
    setAddToLetter(true);
  };

  const isValid = () => {
    if (!reviseQuestionFormValues.question) {
      return false;
    }

    return true;
  };

  const handleQuestionRevisionFormChange = (
    key: keyof QuestionFormValues,
    newValue: any
  ) => {
    setReviseQuestionFormValues((prevForm) => ({
      ...prevForm,
      [key]: newValue,
    }));
  };

  const handleSubmit = async () => {
    if (!isValid()) return;

    await updateQuestionMutation({
      projectId,
      tenderId,
      questionId: question.id,
      data: reviseQuestionFormValues,
    });

    if (addToLetter && projectId && tenderId) {
      doManageQuestionsToLetter({
        projectId,
        tenderId: Number(tenderId),
        action: 'add_question',
        body: {
          question_ids: [question.id],
        },
      }).catch((err) =>
        appLog('Failed to add question to correctional letter', err)
      );
    }

    resetForm();

    onCancel?.();
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({
            id: 'revise',
          })}
          icon={['far', 'archive']}
        />
      }
      okText={intl.formatMessage({ id: 'revise' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      centered
      okButtonProps={{ disabled: !isValid(), loading: isLoading }}
      onOk={handleSubmit}
      onCancel={() => {
        resetForm();
        onCancel?.();
      }}
      destroyOnClose
      zIndex={2000}
      width={800}
      {...modalProps}>
      <QuestionForm
        formValues={reviseQuestionFormValues}
        handleValueChange={handleQuestionRevisionFormChange}
        withUploadFiles={false}
        resetEnabled={true}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px',
        }}>
        <Checkbox
          checked={addToLetter}
          onChange={(e) => setAddToLetter(e.target.checked)}>
          {intl.formatMessage({ id: 'qa-add-to-correctional-letter' })}
          <Tooltip
            title={intl.formatMessage({ id: 'qa-add-to-correctional-letter' })}>
            <span style={{ marginLeft: '8px', color: '#8c8c8c' }}>
              <FontAwesomeIcon icon={['far', 'info-circle']} />
            </span>
          </Tooltip>
        </Checkbox>
      </div>
    </Modal>
  );
};
